import { useState } from "react";
import Enums from "@/enums";
import Helpers from "@/Helpers";
import { toast } from "react-toastify";
import { TbTableImport } from "react-icons/tb";
import { LuFilePlus } from "react-icons/lu";
import { FaPlusSquare } from "react-icons/fa";
import TestSelectModal from "@elements/modals/TestSelectModal";
import ImportUsersModal from "@elements/modals/importUsersModal";
import UserEditModal from "@elements/modals/UserEditModal";
import { CiEdit } from "react-icons/ci";

const ADMIN_COMPANY_ID = 1;

export default function UserListTable({
  userList,
  userData,
  activeUser,
  activeUserId,
  updateActiveUserId,
  onUserListUpdate,
  companyId,
  tabData,
  adminPerms,
  className
}) {
  const [isTestSelectModalVisible, setTestSelectModalVisible] = useState(false);
  const [testUserSelectMode, setTestUserSelectMode] = useState("");
  const [isImportUserModalVisible, setImportUserModalVisible] = useState(false);
  const [userEditId, setUserEditId] = useState(null);

  // Defensive checks for userData properties
  const usersList = Array.isArray(userData?.users) ? userData.users : [];
  const adminUsersList = Array.isArray(userData?.adminUsers) ? userData.adminUsers : [];
  const languagesList = Array.isArray(userData?.languages) ? userData.languages : [];

  // Guard permissions with default array
  const permissions = Array.isArray(userData.permissions) ? userData.permissions : [];
  const effectivePerms = Array.isArray(adminPerms) && adminPerms.length > 0 ? adminPerms : permissions;
  const isSystemAdmin = effectivePerms.includes(Enums.ROLE_PERMS.SystemAdmin);
  const isCompanyAdmin = effectivePerms.includes(Enums.ROLE_PERMS.CompanyAdmin);
  const isAdminLike = isSystemAdmin || isCompanyAdmin;
  const currentUserId = (() => {
    try {
      const userData = window?.sessionStorage?.getItem("User");
      if (!userData) return null;
      const decodedUser = JSON.parse(atob(userData));
      return decodedUser?.userId || decodedUser?.id || null;
    } catch {
      return null;
    }
  })();
  const adminUserIds = new Set(adminUsersList.map(u => u.id));
  async function copyEmailToClipboard(email) {
    if (email) {
      await navigator.clipboard.writeText(email);
      toast.success("Email copied to clipboard");
    }
  }

  function startAddUser() {
    setUserEditId(0); // 0 will signal UserEditModal to open in 'new user' mode
  }

  function addTestUser() {
    setTestUserSelectMode("newUser");
    setTestSelectModalVisible(true);
  }

  function importUserList() {
    setImportUserModalVisible(true);
  }

  function handleEditUser(userId) {
    setUserEditId(userId);
  }

  function onCloseTestModal() {
    setTestUserSelectMode("");
    setTestSelectModalVisible(false);
    onUserListUpdate();
  }

  function onCloseUsersImport() {
    setImportUserModalVisible(false);
    onUserListUpdate();
  }

  function handleUserEditClose(saved, newUserId = null) {
    setUserEditId(null);
    if (saved) {
      // If a new user was created, pass the new user ID for selection
      onUserListUpdate(newUserId);
    }
  }

  // Permission checks for clarity
  const canCreateUser = effectivePerms.includes(Enums.ROLE_PERMS.CreateUser);
  const canEditUser = effectivePerms.includes(Enums.ROLE_PERMS.EditUser) || isAdminLike;
  const canAddUser = canCreateUser || isAdminLike;

  function setActiveUser(e) {
    let userId = parseInt(e.currentTarget.getAttribute("data-id"));
    if (activeUserId !== userId) {
      updateActiveUserId(userId);
      return;
    }
    const canEditThisUser = canEditUser || (currentUserId && currentUserId === userId);
    if (canEditThisUser) {
      setUserEditId(userId);
    }
  }

  return (
    <>
      <div className={`tableWrapper dimensionTable horizontal scrollable rowClickable${className ? ` ${className}` : ''}`}>
        <table>
          <caption>
            <span className="title">Users</span>
            <span className="captionRight">
              <TbTableImport
                data-help-id="userTableExcelImport"
                className={`actionIcon plusIcon${canAddUser ? "" : " disabled"}`}
                title={canAddUser ? "Add User from Excel" : "You don't have permission to create users"}
                onClick={() => {
                  if (canAddUser) importUserList();
                }}
              />
              <LuFilePlus
                data-help-id="userTableTestImport"
                className={`actionIcon plusIcon${canAddUser ? "" : " disabled"}`}
                title={canAddUser ? "Add User from Tests" : "You don't have permission to create users"}
                onClick={() => {
                  if (canAddUser) addTestUser();
                }}
              />
              <FaPlusSquare
                data-help-id="userTableCreateUser"
                className={`actionIcon plusIcon${canAddUser ? "" : " disabled"}`}
                onClick={() => {
                  if (canAddUser) startAddUser();
                }}
                title={canAddUser ? "Create User" : "You don't have permission to create users"}
              />
            </span>
          </caption>
          <thead>
            <tr>
              <th>ID</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Title</th>
              <th>Email</th>
              <th>Language</th>
              {canCreateUser && (
                <>
                  <th>Last Access</th>
                </>
              )}
              <th>Employed</th>
            </tr>
          </thead>
          <tbody>
            {userList.length > 0
              ? (userList.map((user, i) => {
                const u = usersList.find((u) => u.userId === user.userId);
                const additionalClass = u?.companyIds?.includes(ADMIN_COMPANY_ID) ? " adminUser" : "";
                const userLanguage = languagesList.find(l => l.id === (user.defaultLanguageId || user.languageId))?.name || "None";
                const isAdminUser = adminUserIds.has(user.userId);
                let employ = isAdminUser? "-": "";
                if(!isAdminUser) {
                  if(user.hireDate) {
                    if(user.fireDate) 
                      employ = "Ex"
                    else
                      employ = "Y";
                  } else 
                    employ = "Y";
                }

                return (
                  <tr
                    key={"user" + user.userId}
                    className={`${activeUserId === user.userId ? "active" : ""}${additionalClass}`}
                    data-id={user.userId}
                    onClick={setActiveUser}
                  >
                    <td>{user.userId}</td>
                    <td>{user.firstName}</td>
                    <td>{user.lastName}</td>
                    <td>{user.title}</td>
                    <td
                      className={user.email ? "clickable" : ""}
                      title={user.email ? "Click to copy email to clipboard" : ""}
                      onClick={e => {
                        if (user.email) {
                          e.stopPropagation();
                          copyEmailToClipboard(user.email);
                        }
                      }}
                    >
                      {user.email || "None"}
                    </td>
                    <td>{userLanguage}</td>

                    {canCreateUser ? (
                      isAdminUser ? (
                        <td></td>
                      ) : (
                        <>
                          <td title={user.lastAccess ? Helpers.EST(user.lastAccess) : ""}>
                            {user.lastAccess
                              ? Helpers.EST(user.lastAccess, "MM/dd/yyyy")
                              : ""}
                          </td>
                        </>
                      )
                    ) : null}
                    <td>{employ}</td>

                  </tr>
                );
              }))
              : null}
          </tbody>
        </table>
      </div>

      {isTestSelectModalVisible && (
        <TestSelectModal
          isOpen={isTestSelectModalVisible}
          mode={testUserSelectMode}
          onClose={onCloseTestModal}
          companyId={companyId}
          userId={null}
          caption="Select test to create a user from"
          buttonText="Create User"
        />
      )}

      <ImportUsersModal
        isOpen={isImportUserModalVisible}
        onClose={onCloseUsersImport}
        companyId={companyId}
        caption="Import Users from Excel"
      />

      <UserEditModal
        isOpen={userEditId !== null && userEditId !== undefined}
        onClose={handleUserEditClose}
        userId={userEditId}
        companyData={userData}
        updateSpinner={tabData?.updateSpinner}
        adminPerms={adminPerms}
      />
    </>
  );
}

